
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PlusCircle, Edit, Trash2, FileText, Eye, EyeOff } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { getInsightResources, deleteInsightResource } from "@/services/insightResourceService";
import type { InsightResource } from "@/services/insightResourceService";
import InsightResourceForm from "./InsightResourceForm";

const InsightResourcesManager: React.FC = () => {
  const [resources, setResources] = useState<InsightResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<InsightResource | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const data = await getInsightResources();
      setResources(data);
    } catch (error) {
      console.error("Error fetching resources:", error);
      toast({
        title: "Error",
        description: "Failed to load resources",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResources();
  }, []);

  const handleAddNew = () => {
    setSelectedResource(null);
    setIsEditing(false);
    setIsDialogOpen(true);
  };

  const handleEdit = (resource: InsightResource) => {
    setSelectedResource(resource);
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const handleDelete = (resource: InsightResource) => {
    setSelectedResource(resource);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedResource) return;
    
    try {
      await deleteInsightResource(selectedResource.id);
      setResources(resources.filter(r => r.id !== selectedResource.id));
      toast({
        title: "Resource deleted",
        description: "The resource has been deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Delete failed",
        description: "There was a problem deleting the resource",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedResource(null);
    }
  };

  const handleFormSuccess = () => {
    setIsDialogOpen(false);
    fetchResources();
  };

  const handleFormCancel = () => {
    setIsDialogOpen(false);
  };

  const getResourceTypeName = (type: string) => {
    const types = {
      whitepaper: "White Paper",
      checklist: "Checklist",
      guide: "Guide",
      template: "Template",
      report: "Report",
    };
    return types[type as keyof typeof types] || type;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Insight Resources</h2>
        <Button onClick={handleAddNew}>
          <PlusCircle className="mr-2 h-4 w-4" /> Add New Resource
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Gated</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  Loading resources...
                </TableCell>
              </TableRow>
            ) : resources.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-4">
                  No resources found. Add your first resource.
                </TableCell>
              </TableRow>
            ) : (
              resources.map((resource) => (
                <TableRow key={resource.id}>
                  <TableCell className="font-medium">{resource.title}</TableCell>
                  <TableCell>{getResourceTypeName(resource.resource_type)}</TableCell>
                  <TableCell>
                    {resource.is_gated ? (
                      <div className="flex items-center">
                        <Eye className="h-4 w-4 mr-1 text-orange-500" /> Yes
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <EyeOff className="h-4 w-4 mr-1 text-green-500" /> No
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(resource.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(resource)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(resource)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Edit Resource" : "Add New Resource"}
            </DialogTitle>
          </DialogHeader>
          <InsightResourceForm
            resource={selectedResource || undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the resource "{selectedResource?.title}".
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InsightResourcesManager;
